import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import NonScriptRangeBundles from '../components/NonScriptRangeBundles';
import Tabs from '../components/Tabs';
import showToast from '../components/ToastComponent';
import { TabItem } from '../interface/interface';
import { addProductToCart } from '../service/addProductToCart';
import { removeProductFromCart } from '../service/removeProductFromCart';
import { getCart } from '../service/getCart';
import { NonScriptRangeProducts } from '../components/NonScriptRangeProducts';
import { useContext, useEffect } from 'react';
import { UserContext } from '../context/userContext';
import { CartContext } from '../context/cartContext';

export default function NonScriptRange() {
  const {
    setLoadingEnable,
    setHideDropdown,
    setIsMobileDropdownOpen,
    setActiveItem,
  } = useContext(UserContext);
  const { setCart } = useContext(CartContext);
  const { user } = useAuth();
  const qc = useQueryClient();

  const { isLoading } = useQuery({
    queryKey: ['cart', user?.access_token],
    queryFn: async () => {
      const result = await getCart(user!.access_token);
      if (result === undefined) {
        showToast({
          message: 'Unable to fetch existing cart!',
          type: 'error',
        });
        return null;
      }

      setCart(result);
      return result;
    },
    enabled: !!user?.access_token,
  });

  useEffect(() => {
    setHideDropdown(true);
    setIsMobileDropdownOpen(false);
    setActiveItem('Shop non-script');
  }, []);

  const addToCartMutation = useMutation({
    mutationFn: async (productId: string) => {
      const result = await addProductToCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item added to your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }

      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
      return null;
    },
    mutationKey: ['addToCart', user?.access_token],
  });

  const removeProductFromCartMutation = useMutation({
    mutationKey: ['removeCartItem', user?.access_token],
    mutationFn: async (productId: string) => {
      const result = await removeProductFromCart(user!.access_token, productId);
      if (result?.status === 200) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });
        qc.invalidateQueries({
          queryKey: ['cart', user?.access_token],
        });
      }
      if (result?.status === 405) {
        showToast({
          message: result.data.message,
          type: 'warning',
        });
      }
    },
  });

  useEffect(() => {
    if (
      !isLoading ||
      !addToCartMutation.isPending ||
      !removeProductFromCartMutation.isPending
    ) {
      setLoadingEnable(false);
    } else {
      setLoadingEnable(true);
    }
  }, [
    isLoading,
    addToCartMutation.isPending,
    removeProductFromCartMutation.isPending,
  ]);

  const tabs: Array<TabItem> = [
    {
      id: '1',
      label: 'Products',
      component: (
        <NonScriptRangeProducts
          addToCartMutation={addToCartMutation}
          removeFromCartMutation={removeProductFromCartMutation}
          loading={isLoading}
        />
      ),
    },
    {
      id: '2',
      label: 'Bundles',
      component: (
        <NonScriptRangeBundles
          addToCartMutation={addToCartMutation}
          removeFromCartMutation={removeProductFromCartMutation}
          loading={isLoading}
        />
      ),
    },
  ];

  return <Tabs tabs={tabs} />;
}
